import type { AgentRunRequest, AgentRunResult, AgentTask } from "./types";
import {
  getRuntimeToolManifest,
  type RuntimeToolId,
  type ToolSurface,
} from "@/lib/runtimeTools/manifest";
import type { AgentLiveSlug } from "@/lib/agui/agentLive";
import { runRuntimeToolViaSSE } from "./runRuntimeToolSSEClient";
import { runAgentLiveViaSSE, type AgentLiveRunRequest, type AgentLiveRunResult } from "./runAgentLiveSSEClient";

export type AgentToolId = RuntimeToolId;

/**
 * Tasks with a dedicated per-agent AG-UI live route. These dispatch
 * through `/api/agents/<slug>/run/live` (node-level streaming, crisis
 * approvalPending surfacing) instead of the generic runtime-tools live
 * route. Social/translate intentionally stay on the runtime-tools path,
 * which already streams AG-UI events through the full graph pipeline
 * (policy + post-run persistence).
 */
const LIVE_AGENT_TASK_SLUGS: Partial<Record<AgentTask, AgentLiveSlug>> = {
  crisis: "crisis",
};

/** Direct dispatch to a dedicated agent live route (e.g. outreach). */
export async function runAgentLive(req: AgentLiveRunRequest): Promise<AgentLiveRunResult> {
  return runAgentLiveViaSSE(req);
}

const COMPAT_TOOL_TASK_MAP: Record<AgentToolId, AgentTask> = {
  press_release_review: "writer_then_editor",
  media_pitch: "pitch",
  crisis: "crisis",
  fact_checker: "editor_verify",
  content_generator: "writer",
  repurpose: "social",
  translate_artifact: "translate",
  insights_exec: "insights",
  seo_optimize: "insights",
};

type RunToolAsAgentParams = Partial<AgentRunRequest> & {
  surface?: ToolSurface;
  plannerApproved?: boolean;
  sourceConversationId?: string;
  artifactType?: string;
  sourceArtifactType?: string;
  outputArtifactType?: string;
  targetChannel?: string;
  targetLanguage?: string;
  artifactId?: string;
  sourceArtifactId?: string;
  parentArtifactId?: string;
  artifactKey?: string;
  lineageId?: string;
};

export async function runToolAsAgent(
  toolId: AgentToolId,
  params: RunToolAsAgentParams
): Promise<AgentRunResult> {
  const manifest = getRuntimeToolManifest(toolId);
  if (!manifest) return { ok: false, error: `unmapped tool: ${toolId}` };

  const surface = params.surface || "tools_page";
  if (!manifest.surfaces.includes(surface)) {
    return { ok: false, error: `tool ${toolId} not available on ${surface}` };
  }
  if (surface === "chat" && manifest.plannerApprovedInChat && params.plannerApproved !== true) {
    return { ok: false, error: `planner approval required for ${toolId}` };
  }

  const language = params.language === "ar" ? "ar" : "en";
  const task = COMPAT_TOOL_TASK_MAP[toolId] || (manifest.task as AgentTask);
  const req: AgentRunRequest = {
    task,
    clientId: params.clientId || "default",
    language,
    sector: params.sector,
    region: params.region || "GCC",
    sources: params.sources || [],
    brief: params.brief || "",
    input: params.input,
    previousContent: params.previousContent,
  };

  const timezone = (() => {
    if (typeof window === 'undefined') return 'UTC';
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  })();

  // Prefer the dedicated per-agent AG-UI live route when one exists for
  // this task. Falls back to the runtime-tools live route only when the
  // dedicated route is not deployed (transport-level 404/405/501).
  const liveAgentSlug = LIVE_AGENT_TASK_SLUGS[task];
  if (liveAgentSlug) {
    const liveInput = req.input
      || (req.brief && typeof req.previousContent === 'string' && req.previousContent
        ? `${req.brief}\n\nArtifact content:\n${req.previousContent}`
        : req.brief)
      || (typeof req.previousContent === 'string' ? req.previousContent : '')
      || 'Run the requested agent.';
    const liveResult = await runAgentLiveViaSSE({
      agent: liveAgentSlug,
      clientId: req.clientId,
      input: liveInput,
      language: req.language,
      ...(typeof req.previousContent === 'string' && req.previousContent
        ? { previousContent: req.previousContent }
        : {}),
      ...(params.sourceConversationId ? { sourceConversationId: params.sourceConversationId } : {}),
    });
    if (liveResult.ok || !liveResult.transportUnavailable) {
      return liveResult;
    }
  }

  return runRuntimeToolViaSSE({
    toolId,
    surface,
    clientId: req.clientId,
    brief: req.brief,
    input: req.input,
    previousContent: typeof req.previousContent === 'string' ? req.previousContent : undefined,
    language: req.language,
    locale: req.language,
    timezone,
    sourceConversationId: params.sourceConversationId,
    artifactType: params.artifactType,
    sourceArtifactType: params.sourceArtifactType,
    outputArtifactType: params.outputArtifactType,
    targetChannel: params.targetChannel,
    targetLanguage: params.targetLanguage,
    artifactId: params.artifactId,
    sourceArtifactId: params.sourceArtifactId,
    parentArtifactId: params.parentArtifactId,
    artifactKey: params.artifactKey,
    lineageId: params.lineageId,
    plannerApproved: params.plannerApproved === true,
  });
}
